import { ensureCurrentUser, ensurePaymentMethodCard, ensureStripeCustomer } from './data';
import { SUBSCRIPTION_TYPES, SubscriptionStatus, UserType } from './enums';
import { formatUnixDate, safeJsonParse } from './genericHelpers';
import { isCancellationDateInFuture } from './subscriptionHelpers';

/**
 * Extracts user details from a user object.
 * @param {Object} user - The user object.
 * @returns {Object|null} The extracted user details or null if invalid input.
 */
export const getUserDetails = user => {
  if (!user?.id) return null;

  const defaultProfile = { firstName: '', lastName: '', email: '' };
  const { firstName = '', lastName = '', publicData = {}, protectedData = {} } =
    user?.attributes?.profile || defaultProfile;
  const email = user?.attributes?.email || '';
  const { phoneNumber, dateOfBirth } = protectedData || {};
  return {
    firstName,
    lastName,
    email,
    phoneNumber,
    dateOfBirth,
  };
};

/**
 * Determines the type of user from the user object.
 * @param {Object} user - The user object.
 * @returns {string|null} The user type or null if invalid input.
 */
export const getUserType = user => {
  if (!user?.id) return null;
  return user?.attributes?.profile?.publicData?.userType || 'Unknown';
};
export const getUserName = user => {
  if (!user?.id) return null;
  return user?.attributes?.profile?.publicData?.username || 'Unknown';
};

export const getUserSubscriptionDetails = user => {
  if (!user) return null;
  return user?.attributes?.profile?.privateData?.subscriptionData || {};
};

export function getClientSecret(setupIntent) {
  return setupIntent && setupIntent.attributes ? setupIntent.attributes.clientSecret : null;
}

export function getPaymentParams(currentUser, formValues) {
  const { name, addressLine1, addressLine2, postal, state, city, country } = formValues;
  const addressMaybe =
    addressLine1 && postal
      ? {
          address: {
            city: city,
            country: country,
            line1: addressLine1,
            line2: addressLine2,
            postal_code: postal,
            state: state,
          },
        }
      : {};
  const billingDetails = {
    name,
    email: ensureCurrentUser(currentUser).attributes.email,
    ...addressMaybe,
  };

  const paymentParams = {
    payment_method_data: {
      billing_details: billingDetails,
    },
  };

  return paymentParams;
}

export const getDefaultPaymentMethod = user => {
  if (!user?.id?.uuid) return null;
  const hasDefaultPaymentMethod =
    user?.id &&
    ensureStripeCustomer(user.stripeCustomer).attributes.stripeCustomerId &&
    ensurePaymentMethodCard(user.stripeCustomer.defaultPaymentMethod).id;
  return hasDefaultPaymentMethod ?? null;
};

export const getUserSubscriptionStatus = user => {
  if (!user) return null;
  return user.attributes?.profile?.privateData?.subscriptionData?.status;
};
export const getUserSubscriptionData = user => {
  if (!user) return null;
  return user.attributes?.profile?.privateData?.subscriptionData;
};
export const getUserSubscriptionPlanId = user => {
  if (!user) return null;
  return user.attributes?.profile?.privateData?.subscriptionData?.planDetails?.id;
};
export const isSubscriptionSetToCancel = user => {
  if (!user) return null;
  return user.attributes?.profile?.privateData?.subscriptionData?.cancel_at_period_end;
};
export const getSubscriptionCancelDate = user => {
  if (!user) return null;
  return user.attributes?.profile?.privateData?.subscriptionData?.cancel_at;
};

export const getUserSubscriptionId = user => {
  if (!user) return null;
  return user.attributes?.profile?.privateData?.subscriptionData?.id;
};
export const getSubscriptionPriceId = user => {
  if (!user) return null;
  return user.attributes?.profile?.privateData?.subscriptionPriceId || null;
};

export const getUserSubscriptionLookupkeys = user => {
  if (!user) return null;
  return user.attributes?.profile?.publicData?.lookupKeys || [];
};

export const isUserTalentPlus = user => {
  if (!user) return null;
  return user.attributes?.profile?.publicData?.isTalentPlus;
};
export const hasBrandPremierSubscription = user => {
  if (!user) return null;
  return (
    user?.attributes?.profile?.publicData?.subscriptionType === SUBSCRIPTION_TYPES.BRAND_PREMIER
  );
};

export const getUserSubscription = user => {
  if (!user) return null;
  return user.attributes?.profile?.publicData?.subscriptionType;
};

export function retrieveUserFavoritesList(user) {
  if (!user?.id) return null;

  return user.attributes?.profile?.publicData?.favorites || [];
}

export const isUserTalent = user => {
  if (!user?.id) return null;
  return user?.attributes?.profile?.publicData?.userType === UserType.TALENT;
};

export const getDisplayName = user => {
  if (!user?.id?.uuid) return null;
  return user?.attributes?.profile?.displayName || null;
};
export const getAbbreviatedName = user => {
  if (!user?.id?.uuid) return null;
  return user?.attributes?.profile?.abbreviatedName || 'U';
};

export const getSubscriptionForListing = (user, listingId) => {
  if (!user?.id || !listingId) return [];

  const subscriptions = user?.attributes?.profile?.privateData?.subscriptionsByListingId || {};
  let subscriptionsForListing = subscriptions[listingId];

  // Normalize to array if it's a single object (legacy) or null/undefined
  if (subscriptionsForListing && !Array.isArray(subscriptionsForListing)) {
    subscriptionsForListing = [subscriptionsForListing];
  } else if (!subscriptionsForListing) {
    subscriptionsForListing = [];
  }

  return subscriptionsForListing;
};
export const getAssociatedListingId = user => {
  if (!user?.id) return null;
  return user?.attributes?.profile?.publicData?.associatedListingId || null;
};

export const getAssociatedListingCategory = user => {
  if (!user?.id) return null;
  return user?.attributes?.profile?.publicData?.associatedListingCategory || null;
};

export const getUserFullName = user => {
  if (!user?.id?.uuid) return null;
  const { firstName, lastName, displayName } = user?.attributes?.profile || {};
  return `${firstName} ${lastName}` ?? displayName;
};

export const getPhylloUserData = user => {
  if (!user?.id?.uuid) return null;
  return user?.attributes?.profile?.privateData?.phylloUserData || null;
};

export const getProfileImageUrl = user => {
  const profileImage = user?.profileImage || {};
  const profileImageUrl = profileImage?.attributes?.variants['square-small']?.url;
  return profileImageUrl ?? 'https://dummyimage.com/128x128/ddd/000.png&text=User';
};

// Helper function 1: Get unlockable content access (overall across active subscriptions for the listing)
// Returns whether the user has access via any active subscription, plus aggregated details
export const getUnlockableContentAccess = (currentUserSubscription, subscriptionPrices) => {
  const activeSubscriptions =
    currentUserSubscription?.filter(sub => sub.status === SubscriptionStatus.ACTIVE) || [];

  let hasUnlockableContentAccess = false;
  let aggregatedPlanCustomFeatures = {}; // For logging or use last parsed

  for (const sub of activeSubscriptions) {
    const userPriceId = sub.priceId;
    const userSubscriptionPlan = Array.isArray(subscriptionPrices)
      ? subscriptionPrices.find(plan => plan?.id === userPriceId)
      : null;

    const planMetadata = userSubscriptionPlan?.metadata || {};
    const planCustomFeatures = safeJsonParse(planMetadata?.customFeatures, {});

    aggregatedPlanCustomFeatures = planCustomFeatures; // Update for logging (last one)

    if (planCustomFeatures?.unlockableContent?.enabled ?? false) {
      hasUnlockableContentAccess = true;
      break; // Short-circuit if access found
    }
  }

  return {
    hasUnlockableContentAccess,
    // If needed, return the matching subscription that grants access
    grantingSubscription: hasUnlockableContentAccess
      ? activeSubscriptions.find(sub => {
          const userPriceId = sub.priceId;
          const userSubscriptionPlan = Array.isArray(subscriptionPrices)
            ? subscriptionPrices.find(plan => plan?.id === userPriceId)
            : null;
          const planMetadata = userSubscriptionPlan?.metadata || {};
          const planCustomFeatures = safeJsonParse(planMetadata?.customFeatures, {});
          return planCustomFeatures?.unlockableContent?.enabled ?? false;
        })
      : null,
  };
};

// Helper function 2: Get cancellation details for the subscription that grants unlockable content
// Assumes you want details from the active subscription with unlockableContent enabled (or first active if none)
// Use the grantingSubscription from the first helper if available
export const getCancellationDetailsForUnlockable = (
  currentUserSubscription,
  subscriptionPrices
) => {
  const { grantingSubscription } = getUnlockableContentAccess(
    currentUserSubscription,
    subscriptionPrices
  );

  // If no granting sub, fallback to first active subscription
  const targetSubscription =
    grantingSubscription ||
    currentUserSubscription?.find(sub => sub.status === SubscriptionStatus.ACTIVE) ||
    null;
  const cancellationDateUnix = targetSubscription?.cancelAt || null;
  const willCancelAtPeriodEnd = targetSubscription?.cancelAtPeriodEnd || false;

  return {
    targetSubscription,
    cancellationDateUnix,
    willCancelAtPeriodEnd,
    // Derived values for convenience
    formattedCancellationDate: cancellationDateUnix ? formatUnixDate(cancellationDateUnix) : null,
    isCancellationDateValid: isCancellationDateInFuture(cancellationDateUnix),
  };
};

export const getUserDirectMessageServices = user => {
  if (!user?.id) return null;
  return user?.attributes?.profile?.protectedData?.directMessageServices || {};
};
